/**
 * Contact-Form Template: Simple inquiry form without browsing
 *
 * This template provides a straightforward contact form for:
 * - General inquiries
 * - Questions about services
 * - Support requests
 *
 * Simpler than browse-form when no item selection is needed.
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper, disableButtonTemporarily } from '../utils';
import type { ContactFormData, TemplateFormField } from './types';

// =============================================================================
// CONTACT-FORM TEMPLATE
// =============================================================================

registerBlockRenderer('contact-form', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const templateData = data as ContactFormData;

  if (!templateData.fields || templateData.fields.length === 0) {
    console.warn('[contact-form] No fields provided');
    return;
  }

  const wrapper = createBlockWrapper('contact-form');
  wrapper.classList.add('pms-template-contact-form');

  // Internal state
  const state = {
    phase: 'form' as 'form' | 'submitted',
    formValues: {} as Record<string, string>,
  };

  // Pre-fill subject if provided
  if (templateData.subject) {
    state.formValues['subject'] = templateData.subject;
  }

  // Title
  if (templateData.title) {
    const title = document.createElement('h3');
    title.className = 'pms-contact-title';
    title.textContent = templateData.title;
    wrapper.appendChild(title);
  }

  // Intro text
  if (templateData.intro) {
    const intro = document.createElement('p');
    intro.className = 'pms-contact-intro';
    intro.textContent = templateData.intro;
    wrapper.appendChild(intro);
  }

  // Subject badge (if provided by AI context)
  if (templateData.subject) {
    const subjectBadge = document.createElement('div');
    subjectBadge.className = 'pms-contact-subject';
    subjectBadge.innerHTML = `
      <span class="pms-contact-subject-label">Regarding:</span>
      <span class="pms-contact-subject-value">${escapeHtml(templateData.subject)}</span>
    `;
    wrapper.appendChild(subjectBadge);
  }

  // Form
  const form = document.createElement('form');
  form.className = 'pms-contact-form';

  const fieldsContainer = document.createElement('div');
  fieldsContainer.className = 'pms-contact-fields';

  for (const field of templateData.fields) {
    const fieldEl = renderFormField(field, state);
    fieldsContainer.appendChild(fieldEl);
  }

  form.appendChild(fieldsContainer);

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'pms-contact-submit-btn';
  submitBtn.textContent = templateData.submitLabel || 'Send Message';
  form.appendChild(submitBtn);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    handleSubmit();
  });

  wrapper.appendChild(form);

  // Success section
  const successSection = document.createElement('div');
  successSection.className = 'pms-contact-success';
  successSection.style.display = 'none';
  successSection.innerHTML = `
    <div class="pms-template-success">
      <div class="pms-template-success-icon">&#10003;</div>
      <h4>Message Sent</h4>
      <p>${escapeHtml(templateData.successMessage || "Thank you for reaching out. We'll get back to you soon.")}</p>
    </div>
  `;
  wrapper.appendChild(successSection);

  container.appendChild(wrapper);

  function handleSubmit(): void {
    // Build action string
    const formFields: string[] = [];

    // Include subject if provided
    if (templateData.subject) {
      formFields.push(`subject=${encodeURIComponent(templateData.subject)}`);
    }

    // Include form values
    for (const [key, value] of Object.entries(state.formValues)) {
      if (value) {
        formFields.push(`${key}=${encodeURIComponent(value)}`);
      }
    }

    const actionString = `${templateData.submitAction}:${formFields.join(';')}`;

    state.phase = 'submitted';
    form.style.display = 'none';
    if (templateData.subject) {
      const subjectEl = wrapper.querySelector('.pms-contact-subject') as HTMLElement;
      if (subjectEl) subjectEl.style.display = 'none';
    }
    successSection.style.display = '';

    disableButtonTemporarily(submitBtn, 2000);

    if (onAction) {
      onAction(actionString);
    }
  }
});

function renderFormField(field: TemplateFormField, state: { formValues: Record<string, string> }): HTMLElement {
  const fieldWrapper = document.createElement('div');
  fieldWrapper.className = 'pms-contact-field';

  const label = document.createElement('label');
  label.className = 'pms-contact-field-label';
  label.textContent = field.label;
  if (field.required) {
    label.innerHTML += ' <span class="pms-template-required">*</span>';
  }
  fieldWrapper.appendChild(label);

  let input: HTMLElement;

  switch (field.type) {
    case 'textarea':
      input = document.createElement('textarea');
      (input as HTMLTextAreaElement).placeholder = field.placeholder || '';
      (input as HTMLTextAreaElement).required = field.required || false;
      (input as HTMLTextAreaElement).rows = 4;
      input.className = 'pms-contact-input pms-contact-textarea';
      break;

    case 'select':
      input = document.createElement('select');
      input.className = 'pms-contact-input pms-contact-select';
      (input as HTMLSelectElement).required = field.required || false;

      const defaultOpt = document.createElement('option');
      defaultOpt.value = '';
      defaultOpt.textContent = field.placeholder || 'Select...';
      input.appendChild(defaultOpt);

      for (const opt of (field.options || [])) {
        const option = document.createElement('option');
        if (typeof opt === 'string') {
          option.value = opt;
          option.textContent = opt;
        } else {
          option.value = opt.value;
          option.textContent = opt.label;
        }
        input.appendChild(option);
      }
      break;

    default:
      input = document.createElement('input');
      (input as HTMLInputElement).type = field.type || 'text';
      (input as HTMLInputElement).placeholder = field.placeholder || '';
      (input as HTMLInputElement).required = field.required || false;
      input.className = 'pms-contact-input';
  }

  input.dataset['fieldId'] = field.id;
  input.addEventListener('input', (e) => {
    state.formValues[field.id] = (e.target as HTMLInputElement).value;
  });

  fieldWrapper.appendChild(input);
  return fieldWrapper;
}
